import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { usePartnershipNotifications } from '../hooks/usePartnershipNotifications';
import apiService from '../services/api';
import partnershipApiService from '../services/partnershipApi';
import NotificationPopup from './NotificationPopup';
import { Notification } from '../services/api';

interface NotificationManagerProps {
  children: React.ReactNode;
}

const NotificationManager: React.FC<NotificationManagerProps> = ({ children }) => {
  const { user } = useCognitoAuth();
  const location = useLocation();
  const appNotifications = useApp();
  const partnershipNotifications = usePartnershipNotifications();

  const isPartnership = location.pathname.startsWith('/partnership');
  const notifications = isPartnership ? partnershipNotifications.notifications : appNotifications.notifications;
  const fetchNotifications = isPartnership ? partnershipNotifications.fetchNotifications : appNotifications.fetchNotifications;

  const [popupNotifications, setPopupNotifications] = useState<Notification[]>([]);
  const [processedNotificationIds, setProcessedNotificationIds] = useState<Set<number>>(new Set());
  const previousNotificationsRef = useRef<Notification[]>([]);
  const notificationTimeoutRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Track new notifications and show popups
  useEffect(() => {
    if (!user) return;

    // If this is the first load, don't show popups for existing notifications
    if (previousNotificationsRef.current.length === 0 && notifications.length > 0) {
      previousNotificationsRef.current = notifications;
      return;
    }

    const currentNotificationIds = new Set(notifications.map(n => n.id));
    const previousNotificationIds = new Set(previousNotificationsRef.current.map(n => n.id));
    
    // Find new notifications (not in previous list and not already processed)
    const newNotifications = notifications.filter((notification: any) => {
      const isNew = !previousNotificationIds.has(notification.id) && !processedNotificationIds.has(notification.id);
      const isUnread = !notification.read;
      const isNotExpired = notification.is_expired === false || 
                          (notification.is_expired === undefined && (!notification.expires_at || new Date(notification.expires_at) > new Date()));
      return isNew && isUnread && isNotExpired;
    });


    if (newNotifications.length > 0) {
      const sortedNewNotifications = [...newNotifications].sort((a: any, b: any) => {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 2;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 2;
        
        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher priority first
        }
        
        // If same priority, sort by creation time (newer first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      // Limit to maximum 3 popup notifications at once
      const maxPopups = 3;
      const currentPopupCount = popupNotifications.length;
      const availableSlots = Math.max(0, maxPopups - currentPopupCount);
      const notificationsToShow = sortedNewNotifications.slice(0, availableSlots);
      
      
      if (notificationsToShow.length > 0) {
        // Add new notifications to popup queue
        setPopupNotifications(prev => {
          return [...prev, ...notificationsToShow];
        });
        
        // Mark as processed to avoid showing again
        setProcessedNotificationIds(prev => {
          const newSet = new Set(prev);
          notificationsToShow.forEach(n => newSet.add(n.id));
          return newSet;
        });
        
        // Note: Removed auto-close timers - popups now stay open until manually closed
        // This ensures users can read notifications at their own pace
      }
    }

    // Update previous notifications reference
    previousNotificationsRef.current = notifications;
  }, [notifications, user, processedNotificationIds]);

  // Clean up timeouts when component unmounts (if any exist)
  useEffect(() => {
    return () => {
      notificationTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const handlePopupClose = (notificationId: number) => {
    // Remove from popup list
    setPopupNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      if (isPartnership) {
        await partnershipApiService.markNotificationAsRead(notificationId);
      } else {
        await apiService.markNotificationAsRead(notificationId);
      }
      fetchNotifications();
      handlePopupClose(notificationId);
    } catch (error) {
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      if (!isPartnership) {
        await apiService.deleteNotification(notificationId);
        fetchNotifications();
      }
    } catch (error) {
    }
  };

  // Don't render if no user
  if (!user) {
    return <>{children}</>;
  }


  return (
    <>
      {children}
      
      
      {/* Render popup notifications */}
      {popupNotifications.map((notification, index) => {
        return (
          <div
            key={notification.id}
            style={{
              position: 'fixed',
              top: `${20 + (index * 120)}px`, // Stack notifications vertically
              right: '20px',
              zIndex: 9999 - index, // Lower z-index for stacked notifications
            }}
          >
            <NotificationPopup
              notification={notification}
              onClose={() => handlePopupClose(notification.id)}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          </div>
        );
      })}
      
      {/* Show remaining notifications count if limit reached */}
      {popupNotifications.length >= 3 && (
        <div
          style={{
            position: 'fixed',
            top: `${20 + (3 * 120)}px`,
            right: '20px',
            zIndex: 9996,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            padding: '8px 14px',
            borderRadius: '10px',
            fontSize: '12px',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
          }}
        >
          +{notifications.filter((n: any) => !n.read && !processedNotificationIds.has(n.id)).length - 3} mais notificações
        </div>
      )}
    </>
  );
};

export default NotificationManager;
